"use client";

import Link from "next/link";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { MapPin, Mail, Phone } from "lucide-react";
import { SOCIAL_LINKS } from "@/lib/socialLinks";

const LOGO =
  "https://galeria.tocumenaero.com/gallery/LogosTocumen/LogoTocumen-03.png";

export default function Footer() {
  const t = useTranslations("footer");

  const linkClass =
    "text-white/70 hover:text-white transition-colors duration-200";
  const headingClass =
    "text-xs font-semibold uppercase tracking-widest text-white/40";

  return (
    <footer className="w-full bg-[#0a1623] text-white border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 mb-12">
          {/* Marca */}
          <div className="lg:col-span-3 flex flex-col items-start">
            {/* El arte ocupa el 40% de un lienzo cuadrado de 900x900, así que
                el resto es transparencia: los márgenes negativos la recortan
                para que no abra huecos en la maqueta. */}
            <Image
              src={LOGO}
              alt="Tocumen"
              width={900}
              height={900}
              className="h-32 w-auto object-contain -my-9 -ml-3"
            />
            {/* La versión gris del eslogan viene con fondo blanco opaco y sobre
                este fondo saldría como una caja. Se usa el SVG del proyecto,
                que sí es transparente, teñido de blanco. */}
            <Image
              src="/Slogan_Azul.svg"
              alt="La Puerta de Las Américas"
              width={711}
              height={167}
              unoptimized
              className="h-10 w-auto object-contain mt-6 opacity-80 [filter:brightness(0)_invert(1)]"
            />
          </div>

          {/* Enlaces, Portales y Redes comparten fila desde el móvil */}
          <div className="grid grid-cols-3 gap-4 sm:gap-8 lg:contents">
            <div className="lg:col-span-2 space-y-4">
              <h4 className={headingClass}>{t("linksTitle")}</h4>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <Link href="/vuelos" className={linkClass}>
                    {t("links.flights")}
                  </Link>
                </li>
                <li>
                  <Link href="/informacion" className={linkClass}>
                    {t("links.services")}
                  </Link>
                </li>
                <li>
                  <Link href="/transporte" className={linkClass}>
                    {t("links.transport")}
                  </Link>
                </li>
                <li>
                  <Link href="/restaurantes" className={linkClass}>
                    {t("links.restaurants")}
                  </Link>
                </li>
              </ul>
            </div>

            <div className="lg:col-span-2 space-y-4">
              <h4 className={headingClass}>{t("portalsTitle")}</h4>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <Link href="/" className={linkClass}>
                    {t("portals.passengers")}
                  </Link>
                </li>
                <li>
                  <Link href="/carga" className={linkClass}>
                    {t("portals.cargo")}
                  </Link>
                </li>
                <li>
                  <Link href="/corporativo" className={linkClass}>
                    {t("portals.corporate")}
                  </Link>
                </li>
                <li>
                  <Link href="/aeropuertos" className={linkClass}>
                    {t("portals.regional")}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Redes como lista de texto, igual que las otras dos columnas */}
            <div className="lg:col-span-2 space-y-4">
              <h4 className={headingClass}>{t("socialTitle")}</h4>
              <ul className="space-y-2.5 text-sm">
                {SOCIAL_LINKS.map((social) => (
                  <li key={social.name}>
                    <a
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClass}
                    >
                      {social.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contacto */}
          <div className="lg:col-span-3 space-y-4">
            <h4 className={headingClass}>{t("contactTitle")}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5 text-white/70">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-white/40" />
                <span>{t("contact.address")}</span>
              </li>
              <li className="flex items-center gap-2.5 text-white/70">
                <Phone className="w-4 h-4 shrink-0 text-white/40" />
                <span>+507 238-2700</span>
              </li>
              <li className="flex items-center gap-2.5 text-white/70">
                <Mail className="w-4 h-4 shrink-0 text-white/40" />
                <span>info@tocumenpanama.aero</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="w-full h-px bg-white/5 mb-6"></div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/40">
          <p>{t("copyright")}</p>
          <div className="flex gap-6">
            <Link
              href="/corporativo/transparencia"
              className="hover:text-white transition-colors duration-200"
            >
              {t("legal.privacy")}
            </Link>
            <Link
              href="/corporativo/transparencia"
              className="hover:text-white transition-colors duration-200"
            >
              {t("legal.terms")}
            </Link>
            <Link
              href="/corporativo/transparencia"
              className="hover:text-white transition-colors duration-200"
            >
              {t("legal.transparency")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
